import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SoftwareEngineer = () => {
  return (
    <div className="container">
      <Helmet>
        <title>Ariel Rotem | Senior Software Engineer & Google Cloud Expert</title>
        <meta name="description" content="Explore Ariel Rotem's engineering history, from Google Cloud scaling to Medical Data Intelligence and AI systems." />
      </Helmet>
      <div className="mb-4">
        <h1 className="glitch" style={{ color: 'var(--accent-primary)', fontSize: '2.5rem' }}>$ ./view_profile --role software_engineer</h1>
        <p className="mono" style={{ color: 'var(--text-secondary)' }}>
          Establishing secure connection... Done.<br/>
          Retrieving file: ariel_core_competencies.log... Done.
        </p>
      </div>

      <div className="terminal-card">
        <h3 className="mb-2" style={{ color: 'var(--accent-primary)', borderBottom: '1px dashed var(--text-secondary)', paddingBottom: '10px' }}>
          &gt; SYSTEM_SUMMARY
        </h3>
        <p className="mono">
          As a Senior Software Engineer with over a decade of runtime, I thrive on building robust infrastructure and solving complex challenges across various domains: Storage, Cybersecurity, Machine Learning, and Medical Data.
        </p>
        <p className="mono mt-1">
          I've been deeply immersed in code since high school. One of the most important lessons I've learned in recent years is the art of thriving in the codebase—debugging, pinpointing production incidents, and identifying potential fault spots efficiently at scale. It's a testament to the power of systemic thinking.
        </p>
      </div>

      <div className="terminal-card">
        <h3 className="mb-2" style={{ color: 'var(--accent-secondary)' }}>&gt; RUNTIME_HISTORY</h3>
        
        <div className="mb-4" style={{ paddingLeft: '15px', borderLeft: '2px solid var(--accent-secondary)' }}>
          <h4 className="mono" style={{ color: 'var(--text-primary)' }}>[ Google ] :: Software Engineer</h4>
          <p className="mono" style={{ color: 'var(--accent-secondary)', fontSize: '0.85rem' }}>Tel Aviv & New York, NY | June 2022 - PRESENT</p>
          <p className="mono mt-1 text-secondary">
            Process active. Engineering robust infrastructure in Google Cloud. Working on critical systems utilized globally on a daily basis. Driving impact across AI tooling and massive scalability.
          </p>
        </div>

        <div className="mb-4" style={{ paddingLeft: '15px', borderLeft: '2px solid var(--accent-secondary)' }}>
          <h4 className="mono" style={{ color: 'var(--text-primary)' }}>[ Emerge Medical Data Intelligence ] :: Senior Engineer</h4>
          <p className="mono" style={{ color: 'var(--accent-secondary)', fontSize: '0.85rem' }}>Tel Aviv | June 2019 - May 2022</p>
          <p className="mono mt-1 text-secondary">
            Built and scaled a medical data platform processing petabytes of records, serving over 30 million U.S. patients. Achieved massive cost/compute optimizations. Recovered and exported critical datasets that would have otherwise been lost.
          </p>
        </div>

        <div className="mb-4" style={{ paddingLeft: '15px', borderLeft: '2px solid var(--accent-secondary)' }}>
          <h4 className="mono" style={{ color: 'var(--text-primary)' }}>[ IBM Trusteer ] :: Software Engineer</h4>
          <p className="mono" style={{ color: 'var(--accent-secondary)', fontSize: '0.85rem' }}>Tel Aviv | May 2017 - June 2019</p>
          <p className="mono mt-1 text-secondary">
            Developed real-time fraud detection/prevention mechanisms for top global banks. Neutralized novel crime schemes for account takeovers.
          </p>
        </div>

        <div className="mb-4" style={{ paddingLeft: '15px', borderLeft: '2px solid var(--accent-secondary)' }}>
          <h4 className="mono" style={{ color: 'var(--text-primary)' }}>[ IBM Trusteer ] :: DevOps & NOC Engineer</h4>
          <p className="mono" style={{ color: 'var(--accent-secondary)', fontSize: '0.85rem' }}>Tel Aviv | Jan 2015 - Apr 2017</p>
          <p className="mono mt-1 text-secondary">
            Built internal monitoring tools and infra-automation during university. Authored proprietary monitoring software running on the NOC wall within a month of hire. (Admittedly written in Perl.)
          </p>
        </div>

        <div className="mb-2" style={{ paddingLeft: '15px', borderLeft: '2px dotted var(--accent-secondary)' }}>
          <h4 className="mono" style={{ color: 'var(--text-secondary)' }}>[ Computer Dept' ] :: SOC & IT Security Dev</h4>
          <p className="mono" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Tel Aviv | Sep 2009 - Sep 2012</p>
        </div>
      </div>

      <div className="grid grid-cols-2 mt-4">
        <div className="terminal-card">
          <h3 className="mb-2" style={{ color: 'var(--accent-tertiary)' }}>&gt; SKILL_MATRIX</h3>
          <div className="mt-2">
            <span className="skill-tag">Cloud Infrastructure</span>
            <span className="skill-tag">Distributed Systems</span>
            <span className="skill-tag">Cyber Security</span>
            <span className="skill-tag">Artificial Intelligence</span>
            <span className="skill-tag">Machine Learning</span>
            <span className="skill-tag">Data Analytics</span>
            <span className="skill-tag">System Design</span>
            <span className="skill-tag">Tech Leadership</span>
          </div>
          <h3 className="mt-4 mb-2" style={{ color: 'var(--accent-tertiary)' }}>&gt; AWARDS.LOG</h3>
          <ul className="mono text-secondary" style={{ paddingLeft: '20px', fontSize: '0.9rem' }}>
            <li>Google Performance (2024 & 2025): Outstanding Impact (Top 20%)</li>
            <li>Ariel University: President's List, Excellent Capstone. GPA 4.0</li>
          </ul>
        </div>
        
        <div className="terminal-card">
          <h3 className="mb-2" style={{ color: 'var(--accent-primary)' }}>&gt; LOAD_VISUALS</h3>
          <div className="grid grid-cols-2 mt-2">
            <img src="/PhotosForWebsite/SWE_464947082_10224735029500238_9129238902119567853_n.jpg" alt="Ariel Rotem coding at Google Cloud" style={{ width: '100%', height: '120px', objectFit: 'cover', border: '1px solid var(--accent-primary)' }}/>
            <img src="/PhotosForWebsite/SWE_489025492_10226290049854775_4218682031334877395_n.jpg" alt="Ariel Rotem presenting software architecture" style={{ width: '100%', height: '120px', objectFit: 'cover', border: '1px solid var(--accent-primary)' }}/>
            <img src="/PhotosForWebsite/SWE_491915910_10226357291095764_7461566465529424467_n.jpg" alt="Ariel Rotem working on AI and Machine Learning" style={{ width: '100%', height: '120px', objectFit: 'cover', border: '1px solid var(--accent-primary)' }}/>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--accent-primary)', height: '120px' }}>
              <span className="mono" style={{ fontSize: '0.8rem', color: 'var(--accent-primary)' }}>[ IMG_RENDER_OK ]</span>
            </div>
          </div>
        </div>
      </div>

      {/* Side Quests Section */}
      <div className="mt-4 pt-4 mb-4 text-center">
        <h3 className="mono" style={{ color: 'var(--accent-secondary)' }}>--- SIDE QUESTS ---</h3>
        <div className="mt-2" style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <Link to="/frontend-developer" className="btn">cat frontend_expertise.log</Link>
          <Link to="/game-developer" className="btn">./play_game.sh</Link>
        </div>
      </div>

    </div>
  );
};

export default SoftwareEngineer;
